// src/components/stats/StatsCard.jsx
import React from 'react';
import { motion } from 'framer-motion';

const StatsCard = ({
  icon: Icon,
  label,
  value,
  subValue,
  trend,
  trendValue,
  color = 'emerald',
  delay = 0
}) => {
  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-50',
      icon: 'bg-emerald-100 text-emerald-600',
      text: 'text-emerald-600',
      trend: trend === 'up' ? 'text-emerald-500' : 'text-red-500'
    },
    blue: {
      bg: 'bg-blue-50',
      icon: 'bg-blue-100 text-blue-600',
      text: 'text-blue-600',
      trend: trend === 'up' ? 'text-emerald-500' : 'text-red-500'
    },
    orange: {
      bg: 'bg-orange-50',
      icon: 'bg-orange-100 text-orange-600',
      text: 'text-orange-600',
      trend: trend === 'up' ? 'text-emerald-500' : 'text-red-500'
    },
    purple: {
      bg: 'bg-purple-50',
      icon: 'bg-purple-100 text-purple-600',
      text: 'text-purple-600',
      trend: trend === 'up' ? 'text-emerald-500' : 'text-red-500'
    }
  };

  const colors = colorClasses[color] || colorClasses.emerald;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileHover={{ y: -2, transition: { duration: 0.2 } }}
      className="relative overflow-hidden"
    >
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-gray-100 hover:border-gray-200 transition-all hover:shadow-lg">
        {/* Background decoration */}
        <div className={`absolute -top-4 -right-4 w-20 h-20 ${colors.bg} rounded-full opacity-50`} />

        <div className="relative">
          {/* Icon */}
          <div className={`w-12 h-12 ${colors.icon} rounded-xl flex items-center justify-center mb-3`}>
            {Icon && <Icon className="w-6 h-6" />}
          </div>

          {/* Label */}
          <p className="text-sm text-gray-500 mb-1">{label}</p>

          {/* Value */}
          <div className="flex items-baseline gap-2">
            <motion.p
              className="text-2xl font-bold text-gray-900"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: delay + 0.2 }}
            >
              {typeof value === 'number' ? value.toLocaleString() : value}
            </motion.p>
            {subValue && (
              <span className="text-sm text-gray-500">{subValue}</span>
            )}
          </div>

          {/* Trend */}
          {trend && trendValue && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${colors.trend}`}>
              <span>{trend === 'up' ? '↑' : '↓'}</span>
              <span>{trendValue}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Grid wrapper for multiple stats
export const StatsGrid = ({ children, columns = 2 }) => {
  return (
    <div className={`grid grid-cols-${columns} md:grid-cols-4 gap-3`}>
      {children}
    </div>
  );
};

// Quick stats row component
export const QuickStats = ({ stats }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {stats.map((stat, index) => (
        <StatsCard
          key={stat.label}
          {...stat}
          delay={index * 0.1}
        />
      ))}
    </div>
  );
};

export default StatsCard;
